import React from 'react';
import { Link, Navigate } from 'react-router-dom';
import { Plane, ArrowLeft, Shield, Zap, Users, Star } from 'lucide-react';
import { useAuthContext } from '../../contexts/AuthContext';
import { SignUpForm } from './components/SignUpForm';

export function SignUpPage() {
  const { user, loading } = useAuthContext();

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-flex p-4 bg-gradient-to-r from-primary-500/20 to-blue-500/20 backdrop-blur-sm rounded-3xl mb-4">
            <div className="w-8 h-8 border-2 border-primary-400 border-t-transparent rounded-full animate-spin"></div>
          </div>
          <p className="text-white/80">Loading...</p>
        </div>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  const benefits = [
    {
      icon: Shield,
      title: 'Secure & Trusted',
      description: 'Enterprise-grade security for your business data'
    },
    {
      icon: Zap,
      title: 'Instant Setup',
      description: 'Get your services listed in minutes'
    },
    {
      icon: Users,
      title: 'Global Network',
      description: 'Connect with aviation professionals worldwide'
    },
    {
      icon: Star,
      title: 'Verified Listings',
      description: 'Build trust with our verification system'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Background elements */}
      <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1436491865332-7a61a109cc05?auto=format&fit=crop&w=2000')] bg-cover bg-center opacity-5"></div>
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-20 -right-20 w-40 h-40 sm:w-80 sm:h-80 bg-primary-500/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute -bottom-20 -left-20 w-40 h-40 sm:w-80 sm:h-80 bg-blue-500/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
      </div>

      <div className="relative z-10 min-h-screen flex">
        {/* Left side - Benefits */}
        <div className="hidden lg:flex lg:w-1/2 flex-col justify-center p-12 xl:p-16">
          <div className="max-w-lg">
            <Link to="/" className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-8 transition-colors">
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>
            
            <div className="flex items-center gap-3 mb-8">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-primary-400 to-blue-500 rounded-xl blur-lg opacity-60"></div>
                <div className="relative bg-gradient-to-r from-primary-500 to-blue-600 p-3 rounded-xl shadow-lg">
                  <Plane className="h-8 w-8 text-white" />
                </div>
              </div>
              <div>
                <h1 className="text-3xl font-bold bg-gradient-to-r from-white to-primary-200 bg-clip-text text-transparent">
                  AirPortal
                </h1>
                <p className="text-white/60 text-sm">Aviation Services Marketplace</p>
              </div>
            </div>

            <h2 className="text-4xl font-bold text-white mb-6 leading-tight">
              Join the future of aviation services
            </h2>
            
            <p className="text-white/70 text-lg mb-12 leading-relaxed">
              Create your business account and start connecting with aviation professionals 
              worldwide. List your services and grow your business today.
            </p>

            <div className="space-y-6">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-start gap-4">
                  <div className="flex-shrink-0 p-3 bg-gradient-to-r from-primary-500/20 to-blue-500/20 backdrop-blur-sm rounded-xl">
                    <benefit.icon className="h-6 w-6 text-primary-300" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold mb-1">{benefit.title}</h3>
                    <p className="text-white/70 text-sm">{benefit.description}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Social proof */}
            <div className="mt-12 p-6 bg-white/5 backdrop-blur-sm rounded-2xl border border-white/20">
              <div className="flex items-center gap-4 mb-4">
                <div className="flex -space-x-2">
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="w-8 h-8 bg-gradient-to-r from-primary-500 to-blue-600 rounded-full border-2 border-white/20 flex items-center justify-center">
                      <span className="text-white text-xs font-semibold">{String.fromCharCode(64 + i)}</span>
                    </div>
                  ))}
                </div>
                <div>
                  <div className="text-white font-semibold">10,000+ Professionals</div>
                  <div className="text-white/60 text-sm">Already trust AirPortal</div>
                </div>
              </div>
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                ))}
                <span className="text-white/80 text-sm ml-2">4.9/5 average rating</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right side - Sign up form */}
        <div className="w-full lg:w-1/2 flex flex-col justify-center p-6 sm:p-12">
          <div className="w-full max-w-md mx-auto">
            {/* Mobile header */}
            <div className="lg:hidden text-center mb-8">
              <Link to="/" className="inline-flex items-center gap-3 mb-6">
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-r from-primary-400 to-blue-500 rounded-xl blur-lg opacity-60"></div>
                  <div className="relative bg-gradient-to-r from-primary-500 to-blue-600 p-3 rounded-xl shadow-lg">
                    <Plane className="h-6 w-6 text-white" />
                  </div>
                </div>
                <span className="text-2xl font-bold bg-gradient-to-r from-white to-primary-200 bg-clip-text text-transparent">
                  AirPortal
                </span>
              </Link>
            </div>

            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-white/5 to-white/10 rounded-3xl blur-xl"></div>
              <div className="relative bg-white/5 backdrop-blur-xl rounded-3xl border border-white/20 p-8 sm:p-10">
                <div className="text-center mb-8">
                  <h2 className="text-3xl font-bold text-white mb-2">
                    Create your business account
                  </h2>
                  <p className="text-white/70">
                    Already have an account?{' '}
                    <Link 
                      to="/signin"
                      className="text-primary-300 hover:text-primary-200 font-medium transition-colors"
                    >
                      Sign in here
                    </Link>
                  </p>
                </div>

                <SignUpForm />
              </div>
            </div>

            {/* Mobile benefits */}
            <div className="lg:hidden mt-8 grid grid-cols-2 gap-4">
              {benefits.map((benefit, index) => (
                <div key={index} className="text-center p-4 bg-white/5 backdrop-blur-sm rounded-xl border border-white/20">
                  <benefit.icon className="h-6 w-6 text-primary-300 mx-auto mb-2" />
                  <div className="text-white/80 text-sm font-medium">{benefit.title}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}